import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useRef } from "preact/hooks";
import { productImagePlaceholder, closeSearchOverlay } from "../helpers"

export default function Autocomplete() {
    const { format } = useCurrencyFormatting()
    
    const { products, keywords } = useResponse();
    if (!products?.hits?.length && !keywords?.hits?.length) return
    const keywordsRef = useRef(null);
    
    useEffect(() => {
        const handleClick = (event) => {
            if(event.target.closest('.ns-autocomplete-element')) {
                closeSearchOverlay();
            }
        }

        if(keywordsRef.current) {
            keywordsRef.current.addEventListener('click', handleClick);
            return () => keywordsRef.current.removeEventListener('click', handleClick);
        }
    }, [])

    useEffect(() => {
        Nosto.migrateToShopifyMarket({
            productSectionElement: '.ns-autocomplete .ns-autocomplete-hit',
            productUrlElement: '.ns-autocomplete a.nosto-item-link',
            productHandleAttribute: 'x-nosto-handle',
            priceElement: '.ns-autocomplete .nosto-price',
            listPriceElement: '.ns-autocomplete .nosto-list-price',
        });
    }, [products]);

    return (
        <div
            className="ns-autocomplete"
            data-nosto-element="autocomplete"
        >
            <div className="ns-autocomplete-inner">
                <div className="ns-autocomplete-results">
                    {keywords?.hits?.length > 0 && (
                        <div className="ns-autocomplete-section ns-autocomplete-keywords">
                            <h2 
                                id="ns-popular-searches-header" 
                                className="ns-autocomplete-section__header"
                            >
                                Popular Searches
                            </h2>
                            <div 
                                className="ns-autocomplete-section__content" 
                                aria-labelledby="ns-popular-searches-header"
                                ref={keywordsRef}
                            >
                                <Keywords
                                    hits={keywords.hits}
                                    keywordComponent={Keyword}
                                    elementComponent={Element}
                                    keywordInCategoryComponent={KeywordInCategory}
                                    categorySuggestionCount={0}
                                />
                            </div>
                        </div>
                    )}
                    {products?.hits?.length > 0 && (
                        <div className="ns-autocomplete-section ns-autocomplete-products">
                            <h2 
                                id="ns-products-header" 
                                className="ns-autocomplete-section__header"
                            >
                                Products
                            </h2>
                            <div 
                                className="ns-autocomplete-section__content" 
                                role="group" 
                                aria-labelledby="ns-products-header"
                            >
                               {products?.hits?.map(hit => {
                                const hitHandle = hit.url?.split('/').pop();

                                return (
                                    <AutocompleteElement hit={hit}>
                                        <div
                                            className="ns-autocomplete-hit"
                                            data-url={hit.url}
                                            data-nosto-element="product"
                                            x-nosto-handle={hitHandle}
                                        >
                                            <a href="javascript:void(0)" >
                                                <div className="ns-autocomplete-hit__image-wrap">
                                                    <img
                                                        className="ns-autocomplete-hit__image"
                                                        src={`${hit.imageUrl}&width=400` ?? productImagePlaceholder}
                                                        alt={hit.name}
                                                        width="200"
                                                        height="266.6666666666667"
                                                    />
                                                </div>
                                                <div className="ns-autocomplete-hit__details">
                                                    <p className="ns-autocomplete-hit__name">
                                                        {hit.name}
                                                    </p>
                                                    <div className="ns-autocomplete-hit__price-wrap">
                                                        {hit.listPrice && hit.listPrice > hit.price && (
                                                            <>
                                                                <span className="visually-hidden">Regular price:</span>
                                                                <s className="regular-price nosto-list-pric">{format(hit.listPrice).toString().replace(".00", "")}</s>
                                                            </>
                                                        )}
                                                        <span className="visually-hidden">Final price:</span>
                                                        <span className="final-price nosto-price">{format(hit.price).toString().replace(".00", "")}</span>
                                                    </div>
                                                </div>
                                            </a>
                                        </div>
                                    </AutocompleteElement>
                                );
                            })}
                            </div>
                        </div>
                    )}
                </div>
                <div class="ns-autocomplete__footer">
                    <button
                        className="ns-autocomplete__view-results"
                        aria-label="See all results"
                        type="submit"
                        tabIndex="-1"
                        onClick={closeSearchOverlay}
                    >
                        See all results
                    </button>
                </div>
            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement 
            hit={hit}
            filter={filter}
        >
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span className="ns-clickable">
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span className="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}
